import { useEffect, useState } from "react";
import { FileUploadField } from "@marekl/adminer/components";
import { Camera } from "lucide-react";
import { asApiErrorBody } from "@marekl/adminer/api";
import { personPhotoUrl, useUploadPhoto } from "../../lib/queries/photos";
import { ApiError } from "../../lib/api";
import styles from "./PhotoUploadField.module.css";
import type { ID } from "../../lib/id";

// Keep in sync with maxPhotoBytes on the backend (20 MiB) and
// photo.MaxPixels (25 MP). The pixel limit is checked by the server only:
// reading an image's dimensions here would mean decoding it first.
const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
const PHOTO_LIMITS_HINT = "JPEG, PNG nebo WebP; nejvýše 20 MB a 25 megapixelů.";

// PhotoUploadField is a self-contained member-photo control: it previews
// the current photo (fetched from the cookie-authenticated
// GET /persons/:id/photo endpoint), lets the user pick a new image
// (previewed locally before it lands), and uploads it via its own mutation —
// independent of any surrounding react-hook-form submit.
//
// A photo can be REPLACED but not removed: a member on the soupiska is
// supposed to have one, so "no photo" is a gap to close, never a state to
// return to. (DELETE /teams/:id/persons/:personId/photo still exists — it is
// PII, often of a minor, and a purge must remain possible — it is simply not
// offered as an everyday action.)
//
// The picking is the library's since 0.31.1: `FileUploadField` in its
// `thumb` shape — a fixed square preview beside a compact trigger, which is
// what a photo field among other form fields wants. What stays here is the
// domain: which endpoint the bytes go to, the optimistic local preview, and
// the cache-busting reload after a replace.
export function PhotoUploadField({
  teamId,
  personId,
  hasPhoto,
  label = "Foto",
}: {
  // Deliberately `number | null` rather than optional: null is the members
  // registry saying "use the global admin-only route", while a forgotten
  // team scope (or a `team?.id` still loading) stays a compile error
  // instead of a silent reroute to an endpoint the caller cannot use.
  teamId: ID | null;
  personId: ID;
  hasPhoto: boolean;
  label?: string;
}) {
  const upload = useUploadPhoto(teamId, personId);
  const [uploaded, setUploaded] = useState(hasPhoto);
  const [preview, setPreview] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  // Bumped after each replace so the <img> re-fetches the new bytes.
  const [version, setVersion] = useState(0);

  useEffect(() => setUploaded(hasPhoto), [hasPhoto]);

  async function handleFile(file: File) {
    // Client-side guard: skip the round trip the backend would reject with
    // 413 anyway (keep in sync with maxPhotoBytes on the server).
    //
    // Ours rather than the primitive's `maxSize`, which would report the
    // limit through `formatBytes` — that is DECIMAL, so 20 MiB reads back as
    // „21 MB" and contradicts the sentence beside it.
    if (file.size > MAX_PHOTO_BYTES) {
      setSizeError("Soubor je příliš velký. Maximální velikost fotky je 20 MB.");
      return;
    }
    setSizeError(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
    try {
      await upload.mutateAsync(file);
      setUploaded(true);
      setVersion((v) => v + 1);
      setPreview(null);
    } catch {
      // Revert the optimistic preview; the error surfaces via upload.error.
      setPreview(null);
    }
  }

  function errMessage(e: unknown): string | undefined {
    if (!e) return undefined;
    if (e instanceof ApiError) {
      // asApiErrorBody is the lib's single source of truth for the error
      // shape — it also recognises the legacy `error` field a bare cast
      // would miss.
      return asApiErrorBody(e.body)?.message ?? "Nahrání se nezdařilo.";
    }
    return "Neočekávaná chyba.";
  }

  const src = preview ?? (uploaded ? personPhotoUrl(personId, { size: "thumb", version }) : null);
  const busy = upload.isPending;

  return (
    <div className={styles.field}>
      <FileUploadField
        label={label}
        variant="thumb"
        // Extensions as well as MIME types: the primitive matches on
        // `file.type`, which a drag-and-drop can leave empty or report as
        // `image/jpg`, while the server falls back to sniffing the bytes
        // (membership.detectPhotoType). Without these the field refuses
        // photos the backend accepts.
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        onSelect={(file) => void handleFile(file)}
        busy={busy}
        // One error surface — see the note in MusicPanel.
        error={sizeError ?? errMessage(upload.error)}
        triggerLabel={uploaded ? "Změnit fotku" : "Nahrát fotku"}
        hint={PHOTO_LIMITS_HINT}
        preview={src ? <img src={src} alt={label} /> : undefined}
        // Not pushed through `preview`: that slot means "the file as it
        // stands", and a camera glyph is the absence of one.
        placeholder={<Camera size={24} aria-hidden />}
      />
    </div>
  );
}
